import { PermissionActionType, PermissionResourceType } from '../../api/constants';
import { Query as OldPermissionModelQuery } from '@stores/permissions';

const BaseResourceType = {
  ...PermissionResourceType,
  EXTRACT: 'extract',
} as const;

export type BaseResourceType = (typeof BaseResourceType)[keyof typeof BaseResourceType];

export const ResourceType = {
  ...BaseResourceType,
  SOURCE: 'source',
} as const;

export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];

export const RESOURCE_GROUPS = {
  SOURCE: [
    ResourceType.EVENT_STREAM,
    ResourceType.AUDIENCE,
    ResourceType.TABLE,
    ResourceType.SQL_MODEL,
    ResourceType.EXTRACT,
  ],
  RETL: [ResourceType.AUDIENCE, ResourceType.TABLE, ResourceType.SQL_MODEL],
} as const;

export const ActionType = {
  ...PermissionActionType,
  CONNECT_EXISTING: 'connect-existing',
  CONNECT_NEW: 'connect-new',
  DISCONNECT: 'disconnect',
  CREATE: 'create',
  DELETE: 'delete',
} as const;

export type ActionType = (typeof ActionType)[keyof typeof ActionType];

type Resource<
  RT extends ResourceType | ResourceType[],
  IdType = undefined,
> = IdType extends undefined ? { type: RT } : { type: RT; id: IdType };

type ConnectPair<S extends BaseResourceType, D extends ResourceType | ResourceType[], IdType> = [
  Resource<S, string>,
  Resource<D, IdType>,
];

type SourceResourceType = (typeof RESOURCE_GROUPS.SOURCE)[number];

type SourceResourceTypeWithProfilesProject =
  | SourceResourceType
  | typeof ResourceType.PROFILES_PROJECT;

type SourceResourceTypeWithAbstractTypes =
  | SourceResourceType
  | typeof ResourceType.RETL
  | typeof ResourceType.SOURCE;

type ConnectSpecificResources<IdType> =
  | ConnectPair<SourceResourceTypeWithProfilesProject, typeof ResourceType.DESTINATION, IdType>
  | ConnectPair<typeof ResourceType.DESTINATION, (typeof RESOURCE_GROUPS.SOURCE)[number], IdType>
  | ConnectPair<typeof ResourceType.EVENT_STREAM, typeof ResourceType.TRACKING_PLAN, IdType>
  | ConnectPair<typeof ResourceType.DESTINATION, typeof ResourceType.TRANSFORMATION, IdType>;

type ConnectExistingResources<IdType> =
  | ConnectPair<SourceResourceTypeWithProfilesProject, typeof ResourceType.DESTINATION, IdType>
  | ConnectPair<typeof ResourceType.DESTINATION, SourceResourceTypeWithAbstractTypes, IdType>
  | ConnectPair<typeof ResourceType.EVENT_STREAM, typeof ResourceType.TRACKING_PLAN, IdType>
  | ConnectPair<typeof ResourceType.TRACKING_PLAN, typeof ResourceType.EVENT_STREAM, IdType>
  | ConnectPair<typeof ResourceType.TRANSFORMATION, typeof ResourceType.DESTINATION, IdType>
  | ConnectPair<typeof ResourceType.DESTINATION, typeof ResourceType.TRANSFORMATION, IdType>;

type ConnectNewResources<IdType> =
  | ConnectPair<SourceResourceTypeWithProfilesProject, typeof ResourceType.DESTINATION, IdType>
  | ConnectPair<
      typeof ResourceType.DESTINATION,
      SourceResourceTypeWithAbstractTypes | SourceResourceTypeWithAbstractTypes[],
      IdType
    >
  | ConnectPair<typeof ResourceType.EVENT_STREAM, typeof ResourceType.TRACKING_PLAN, IdType>;

export type NewPermissionModelQuery =
  | {
      actionType: typeof ActionType.CONNECT;
      resources: ConnectSpecificResources<string>;
    }
  | {
      actionType: typeof ActionType.CONNECT_EXISTING;
      resources: ConnectExistingResources<string[]>;
    }
  | {
      actionType: typeof ActionType.CONNECT_NEW;
      resources: ConnectNewResources<undefined>;
    }
  | {
      actionType: typeof ActionType.DISCONNECT;
      resources: ConnectSpecificResources<string>;
    }
  | {
      actionType: typeof ActionType.EDIT;
      resource: Resource<BaseResourceType, string | undefined>;
    }
  | {
      actionType: typeof ActionType.CREATE;
      resource: Resource<ResourceType>;
    }
  | {
      actionType: typeof ActionType.DELETE;
      resource: Resource<BaseResourceType, string>;
    }
  | {
      actionType: typeof ActionType.PII_FAILURE_SAMPLES | typeof ActionType.PII_LIVE_EVENTS;
      resource: Resource<BaseResourceType, string>;
    };

export type Query = {
  old?: OldPermissionModelQuery;
  new: NewPermissionModelQuery;
};

export type QueryResult = {
  isAllowed: boolean;
  denialReason?: string;
};
